import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { LinkIcon, ArrowTopRightOnSquareIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { getCnGuid } from "../api/index";

interface CnDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  cnNumber: string;
}

export const CnDocumentModal: React.FC<CnDocumentModalProps> = ({
  isOpen,
  onClose,
  cnNumber,
}) => {
  const [loading, setLoading] = useState(false);
  const [cnGuid, setCnGuid] = useState<string | null>(null);

  const CONSIGNOR_BASE =
    "https://mrclogistics.co.in/Reports/PrintView.aspx?report=LR_PRINT_CONSIGNOR&param=GID-";
  const CONSIGNEE_BASE =
    "https://mrclogistics.co.in/Reports/PrintView.aspx?report=LR_PRINT_CONSIGNEE&param=GID-";

  useEffect(() => {
    if (isOpen && cnNumber) {
      (async () => {
        setLoading(true);
        try {
          const response = await getCnGuid(cnNumber);
          if (response?.cn_guid) setCnGuid(response.cn_guid);
        } catch {
          toast.error("Failed to fetch CN GUID");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [isOpen, cnNumber]);

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied!', {
      style: {
        background: '#f0fdf4',
        color: '#15803d',
        border: '1px solid #bbf7d0'
      }
    });
  }

  if (!isOpen) return null;

  const consignorUrl = `${CONSIGNOR_BASE}${cnGuid}`;
  const consigneeUrl = `${CONSIGNEE_BASE}${cnGuid}`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-7 rounded-xl shadow-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-800">
            Documents — CN <span className="text-gray-600">{cnNumber}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-gray-500 py-10 text-center animate-pulse">
            Fetching CN GUID...
          </div>
        ) : cnGuid ? (
          <div className="flex flex-col gap-5 mt-3">
            {[
              { label: "Consignor Copy", url: consignorUrl },
              { label: "Consignee Copy", url: consigneeUrl },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-gray-100">
                    <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    {item.label}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-[12px] border-gray-200 text-gray-700 hover:bg-gray-50"
                    onClick={() => copyLink(item.url)}
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    Copy Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-[12px] border-gray-200 text-gray-700 hover:bg-gray-50"
                    onClick={() => window.open(item.url, "_blank")}
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 py-10 text-center">
            No Documents available.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
